import {
  type PayloadAction,
  createSelector,
  createSlice
} from "@reduxjs/toolkit";

import type { RootState } from "app/store";

/**
 * The user's own last explicit collapse/expand of the patient header.
 *
 * `undefined` means they have not touched it, so the automatic rule decides. Once set it wins
 * over the E-Documents default — which is what stops the navigation effect in
 * `patient-header-layout` from stomping a deliberate choice on every click.
 */
export type UserHeaderChoice = "expanded" | "collapsed";

/**
 * Which screen a choice is about.
 *
 * E-Documents is the only tab that auto-collapses, and the only one whose content the header was
 * actually in the way of, so it remembers its own answer. `"other"` covers every remaining patient
 * tab. Two peer buckets, not a value plus an override — hence `"other"` rather than `"default"`.
 *
 * Before the split there was one global value, and landing on E-Documents recorded `"collapsed"`
 * into it: the automatic decision about one tab then governed the whole chart, and the user got a
 * collapsed header on Chart Notes they had never asked for.
 *
 * Derived from the pathname by `getHeaderScope` in `use-patient-header-auto-collapse.hook`.
 */
export type HeaderScope = "e-documents" | "other";

/**
 * Both buckets, as stored. A missing key means "no preference for that screen", so the automatic
 * rule decides there — independently of whatever the other bucket holds.
 *
 * Persisted whole by `use-patient-header-collapse-preference.hook`; global to the user, NOT per
 * patient, so a choice survives tab changes, patient changes and reloads. That persistence IS the
 * feature: users complained about re-clicking the header on every tab.
 */
export type HeaderChoicesByScope = Partial<
  Record<HeaderScope, UserHeaderChoice>
>;

/**
 * Whether `userHeaderChoices` has been read out of localStorage yet.
 *
 * An empty record alone cannot distinguish "not read yet" from "no preference stored", and the
 * window between them is real: `RequireAuth` gates on the token only, so the app renders while
 * `/auth/users/me/` is in flight, and the preference key needs the user id. React also runs child
 * effects before parent ones, so `patient-header-layout` resolves header state before
 * `PatientHeaderProvider` can hydrate, however the two are written.
 *
 * Nothing writes the preference during that window — the only writers are the two user
 * affordances — so this cannot corrupt what is on disk. What it prevents is a wrong-state flash:
 * without it, a cold load onto a tab where the user stored `"collapsed"` paints the full 281px
 * header, then yanks it away once the read lands.
 */
export type HeaderPreferenceStatus = "loading" | "ready";

export interface PatientHeaderState {
  isHeaderCollapsed?: boolean;
  headerAnimationInProgress?: string;
  userHeaderChoices: HeaderChoicesByScope;
  preferenceStatus: HeaderPreferenceStatus;
}

const initialState: PatientHeaderState = {
  isHeaderCollapsed: undefined,
  headerAnimationInProgress: "",
  userHeaderChoices: {},
  preferenceStatus: "loading"
};

export const patientHeaderSlice = createSlice({
  name: "patient-header",
  initialState,
  reducers: {
    setHeaderAnimationInProgress: (state, action: PayloadAction<string>) => {
      state.headerAnimationInProgress = action.payload;
    },
    setIsHeaderCollapsed: (
      state,
      action: PayloadAction<boolean | undefined>
    ) => {
      state.isHeaderCollapsed = action.payload;
    },
    /**
     * Record a choice for ONE scope. The other bucket is deliberately untouched: hiding the header
     * on Chart Notes says nothing about what the user wants on E-Documents, and vice versa.
     */
    setUserHeaderChoice: (
      state,
      action: PayloadAction<{ scope: HeaderScope; choice: UserHeaderChoice }>
    ) => {
      state.userHeaderChoices[action.payload.scope] = action.payload.choice;
    },
    /**
     * Seed both buckets from storage. Flips `preferenceStatus` in the SAME action as the value:
     * two dispatches would leave a render in between where the status says "ready" and the
     * value is still stale, which is the race this whole flag exists to close.
     *
     * An absent preference is a valid, complete answer — an empty record still means ready.
     */
    hydrateUserHeaderChoices: (
      state,
      action: PayloadAction<HeaderChoicesByScope>
    ) => {
      state.userHeaderChoices = action.payload;
      state.preferenceStatus = "ready";
    }
  }
});

export const {
  setHeaderAnimationInProgress,
  setIsHeaderCollapsed,
  setUserHeaderChoice,
  hydrateUserHeaderChoices
} = patientHeaderSlice.actions;

const patientHeaderSelector = (state: RootState) => state.patientHeader;

export const selectIsHeaderCollapsed = createSelector(
  patientHeaderSelector,
  (patientHeader) => patientHeader.isHeaderCollapsed
);

export const selectHeaderAnimationInProgress = createSelector(
  patientHeaderSelector,
  (patientHeader) => patientHeader.headerAnimationInProgress
);

export const selectUserHeaderChoices = createSelector(
  patientHeaderSelector,
  (patientHeader) => patientHeader.userHeaderChoices
);

export const selectHeaderPreferenceStatus = createSelector(
  patientHeaderSelector,
  (patientHeader) => patientHeader.preferenceStatus
);

export default patientHeaderSlice.reducer;
